import { useSyncExternalStore, useEffect } from 'react'

type Listener = () => void

/** Maps a route path (e.g. "/workspace/queue/enc-001") to a display label (e.g. "Richard Moore"). */
let labels: Map<string, string> = new Map()
const listeners = new Set<Listener>()

function emit() {
  // Publish a fresh Map reference so useSyncExternalStore's Object.is
  // bailout doesn't suppress the re-render. Mutating the existing map in
  // place keeps the same reference and React skips updates.
  labels = new Map(labels)
  listeners.forEach((l) => l())
}

function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function getSnapshot(): Map<string, string> {
  return labels
}

/** Read all dynamic breadcrumb labels */
export function useBreadcrumbLabels() {
  return useSyncExternalStore(subscribe, getSnapshot)
}

/** Set a dynamic breadcrumb label for the current page path. Cleans up on unmount. */
export function useBreadcrumbLabel(path: string | undefined, label: string | undefined) {
  useEffect(() => {
    if (!path || !label) return
    labels.set(path, label)
    emit()
    return () => {
      labels.delete(path)
      emit()
    }
  }, [path, label])
}

/* ── Crumb link targets ──
 * A crumb links to its own path by default. A page can point one elsewhere —
 * e.g. carry its filters (`/batch-runs?type=event`), or send a crumb whose
 * path is not a route (`/batch-runs/batches`) to a real page. */

let hrefs = new Map<string, string>()

/** Read the crumb link overrides (header side). */
export function useBreadcrumbHrefs() {
  return useSyncExternalStore(subscribe, () => hrefs)
}

/** Point the crumb at `path` to `href` while mounted (page side). */
export function useBreadcrumbHref(path: string | undefined, href: string | undefined) {
  useEffect(() => {
    if (!path || !href) return
    hrefs = new Map(hrefs).set(path, href)
    listeners.forEach((l) => l())
    return () => {
      hrefs = new Map(hrefs)
      hrefs.delete(path)
      listeners.forEach((l) => l())
    }
  }, [path, href])
}

/* ── Header back-button override ──
 * A page with an internal drill-down (component state, not a route) can take
 * over the header's back chevron so it steps out of the drill-down instead of
 * navigating to the parent route. Registered while the drill-down is open;
 * cleared on unmount or when the handler becomes undefined. */

let backOverride: (() => void) | null = null
const backListeners = new Set<Listener>()

function emitBack() {
  backListeners.forEach((l) => l())
}

function subscribeBack(listener: Listener) {
  backListeners.add(listener)
  return () => { backListeners.delete(listener) }
}

/** Read the current back override (header side). Null → default navigation. */
export function useHeaderBackOverride() {
  return useSyncExternalStore(subscribeBack, () => backOverride)
}

/** Register a back override while `handler` is defined (page side).
 *  Pass a stable (useCallback) handler; pass undefined to release. */
export function useRegisterHeaderBack(handler: (() => void) | undefined) {
  useEffect(() => {
    if (!handler) return
    backOverride = handler
    emitBack()
    return () => {
      if (backOverride === handler) backOverride = null
      emitBack()
    }
  }, [handler])
}
